import React, { useState } from "react";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../api/snackbarSlice";
import { apiClient } from "../../utils/axios";
import { usePermissions } from "../../hooks/usePermissions";
import { useAccessDeniedMessage } from "../../hooks/useAccessDeniedMessage";
import {
  UPDATE_PURCHASE_CREDIT_NOTE,
  UPDATE_PURCHASE_INVOICE,
  UPDATE_SALE_CREDIT_NOTE,
  UPDATE_SALE_INVOICE,
} from "../../constants/eInvoicePermissions";
import PermissionDeniedWrapper from "../../components/PermissionDeniedWrapper";

const resolveXmlFileKind = (documentType, type, mlsFor) => {
  if (type === "tdd") {
    return "tdd";
  }

  if (type === "mls") {
    return mlsFor === "tdd" ? "tdd-mls" : "document-mls";
  }

  return documentType;
};

const ArchiveXmlMenuItem = ({
  id,
  disabled,
  disabledReason,
  onClick,
  children,
}) => {
  const menuItem = (
    <MenuItem id={id} disabled={disabled} onClick={onClick}>
      {children}
    </MenuItem>
  );

  if (!disabled) {
    return menuItem;
  }

  return (
    <Tooltip title={disabledReason}>
      <span>{menuItem}</span>
    </Tooltip>
  );
};

const DocumentActions = ({
  documentId,
  documentUuid,
  documentType,
  documentApiBaseKey,
  isPurchase = false,
  allowResubmit = false,
  allowArchiveXml = true,
  archiveXmlDisabledReason,
  showDocumentMls = true,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasEInvoicePermission } = usePermissions();
  const accessDeniedMessage = useAccessDeniedMessage();
  const updatePermission = isPurchase
    ? documentApiBaseKey === "credit-notes"
      ? UPDATE_PURCHASE_CREDIT_NOTE
      : UPDATE_PURCHASE_INVOICE
    : documentApiBaseKey === "credit-notes"
      ? UPDATE_SALE_CREDIT_NOTE
      : UPDATE_SALE_INVOICE;
  const canUpdate = hasEInvoicePermission(updatePermission);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const onClickingDetails = () => {
    const url = `/${isPurchase ? "purchases" : "sales"}/${documentApiBaseKey}/${documentId}`;
    navigate(url);
    handleCloseMenu();
  };

  const handleUpdateResubmit = () => {
    if (!canUpdate) {
      return;
    }
    navigate(
      `/${isPurchase ? "purchases" : "sales"}/${documentApiBaseKey}/${documentId}/resubmit`,
    );
    handleCloseMenu();
  };

  const handleDownloadXMLFile = async (id, options = {}) => {
    const { mlsFor = "document", type } = options;
    dispatch(showSnackbar({ message: "Downloading XML...", severity: "info" }));

    try {
      const params = { mls_for: mlsFor };
      if (type) params.type = type;

      const response = await apiClient.get(
        `${isPurchase ? "purchase" : "sales"}-${documentApiBaseKey}/${id}/xml`,
        {
          responseType: "text",
          headers: {
            Accept: "application/xml",
          },
          params,
        },
      );

      const blob = new Blob([response.data], {
        type: "application/xml;charset=utf-8",
      });
      const fileKind = resolveXmlFileKind(documentType, type, mlsFor);
      const fallbackFileName = `${fileKind}-${documentUuid ?? id}.xml`;
      const suggestedFileName = response.headers?.["x-suggested-filename"];
      const fileName =
        (typeof suggestedFileName === "string" && suggestedFileName.trim()) ||
        fallbackFileName;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(link.href);
    } catch (error) {
      dispatch(
        showSnackbar({
          open: true,
          message: `Error downloading the file: ${error.message}`,
          severity: "error",
        }),
      );
    }
    handleCloseMenu();
  };

  return (
    <>
      <IconButton id="action-icon-button" onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          paper: {
            style: {
              boxShadow: "4px 4px 10px 0px rgba(0,0,0,0.2)",
            },
          },
        }}
      >
        <MenuItem id="details" onClick={onClickingDetails}>
          Details
        </MenuItem>
        <MenuItem
          id="download"
          onClick={() => handleDownloadXMLFile(documentId)}
        >
          Download XML
        </MenuItem>
        <ArchiveXmlMenuItem
          id="download-tdd"
          disabled={!allowArchiveXml}
          disabledReason={archiveXmlDisabledReason}
          onClick={() => handleDownloadXMLFile(documentId, { type: "tdd" })}
        >
          Download TDD
        </ArchiveXmlMenuItem>
        {showDocumentMls && (
          <ArchiveXmlMenuItem
            id="download-document-mls"
            disabled={!allowArchiveXml}
            disabledReason={archiveXmlDisabledReason}
            onClick={() =>
              handleDownloadXMLFile(documentId, {
                type: "mls",
                mlsFor: "document",
              })
            }
          >
            Download Document MLS
          </ArchiveXmlMenuItem>
        )}
        <ArchiveXmlMenuItem
          id="download-tdd-mls"
          disabled={!allowArchiveXml}
          disabledReason={archiveXmlDisabledReason}
          onClick={() =>
            handleDownloadXMLFile(documentId, { type: "mls", mlsFor: "tdd" })
          }
        >
          Download TDD MLS
        </ArchiveXmlMenuItem>
        {allowResubmit && (
          <PermissionDeniedWrapper
            show={!canUpdate}
            title={accessDeniedMessage}
          >
            <MenuItem
              id="update-resubmit"
              onClick={handleUpdateResubmit}
              disabled={!canUpdate}
            >
              Fix and Resubmit
            </MenuItem>
          </PermissionDeniedWrapper>
        )}
      </Menu>
    </>
  );
};

export default DocumentActions;
